import React, { useEffect, useMemo, useState } from "react";
import * as S from "./PageWrapper.style";
import { UpgradeButton } from "./UpgradeButton";
import { TryForFreeBanner } from "./TryForFreeBanner";
import { Notifications } from "~/components/notifications";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { PlansModal } from "~/components/plans/plansModal";
import { ResumeModal } from "~/components/resumeModal";
import { LimitsModal } from "~/components/limits-modal";
import { ReconnectAliexpressBanner } from "../reconnect-aliexpress-banner";
import { useAccount } from "~/hooks/useAccount";
import { Onboarding } from "../onboarding";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { usePlans } from "~/api/billing/queries";
import { paths } from "~/router/paths";
import { SubscriptionType } from "~/types/billing";
import { PlanStatus } from "~/types/account";
import { Userpilot } from "userpilot";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalConnectToStore } from "../connect-to-store/modal";
import Loader from "../ui/Loader";
import { BottomMenu } from "../bottom-menu";
import { AnualPlansModal } from "../plans/anualPlansModal";
import { DTPromotionModal } from "../dt-promotion-modal";

interface IProps {
  children: React.ReactNode;
}

export const PageWrapper: React.FC<IProps> = ({ children = null }) => {
  const { getAccount } = useAccount();
  const isSettingsPage = location.pathname.includes("settings");
  const isNotMobile = useMediaQuery("tablet");
  const queryParams = new URLSearchParams(location.search);
  const [isShowingOnboarding, setIsShowingOnboarding] = useState<
    boolean | null
  >(null);
  const planId = queryParams.get("plan_id");
  const showRating = queryParams.get("show_rating");
  const onboarding = queryParams.get("onboarding");
  const annual = queryParams.get("annual");
  const { data: plans } = usePlans();
  const activePlans = plans?.filter(
    (plan) => plan.status === PlanStatus.ACTIVE
  );
  const empirePlan = useMemo(
    () =>
      activePlans?.find(
        (plan) =>
          plan.name === "Empire" &&
          plan.interval === SubscriptionType.MONTHLY &&
          plan.for_winning
      ),
    [plans]
  );
  const navigate = useNavigate();
  const pageLocation = useLocation();

  const handleGoToApp = () => {
    setIsShowingOnboarding(false);
  };

  useEffect(() => {
    Userpilot.reload();
    if (location.search.includes("onboarding=true")) {
      localStorage.setItem("isOnboarding", "true");
    }
  }, [pageLocation]);

  useEffect(() => {
    const checkOnboardingCondition = async () => {
      const account = await getAccount();
      if (!account) return;

      if (onboarding && !account.active_subscription) {
        return setIsShowingOnboarding(true);
      }

      const searchParams = new URLSearchParams(window.location.search);

      if(annual) {
        searchParams.set("annual", "true");
        navigate(`?${searchParams.toString()}`);
      }

      if (
        account &&
        !account?.active_subscription &&
        !account?.has_subscribed_before &&
        !planId &&
        !showRating
      ) {
        searchParams.set("onboarding", "true");
        searchParams.set("origin", account?.signup_origin);
        navigate(`?${searchParams.toString()}`, { replace: true });
        setIsShowingOnboarding(true);
        return;
      }

      const showCustomizationModal = account.show_store_customization;

      if (showCustomizationModal) {
        searchParams.set("onboarding", "true");
        searchParams.set("origin", account?.signup_origin);
        navigate(`?${searchParams.toString()}`);
        setIsShowingOnboarding(true);
        return;
      }

      searchParams.delete("onboarding");
      const queryParams = searchParams.toString();
      const newUrl =
        window.location.pathname + (!!queryParams ? `?${queryParams}` : "");
      window.history.replaceState(null, "", newUrl);

      setIsShowingOnboarding(false);
    };

    checkOnboardingCondition();
  }, [planId, onboarding]);

  if (isShowingOnboarding === null) {
    return <Loader />;
  }

  return (
    <>
      {!isShowingOnboarding ? (
        <S.PageWrapper
          padding={isSettingsPage ? "24px 20px 58px" : "24px 92px 58px"}
          style={{
            backgroundColor: "#f7f7f7",
          }}>
          <S.PageHeader>
            <UpgradeButton />
            {isNotMobile ? <Notifications /> : null}
          </S.PageHeader>
          <ReconnectAliexpressBanner />
          <PlansModal />
          <DTPromotionModal />
          <AnualPlansModal />
          <ResumeModal />
          <LimitsModal />
          <ModalConnectToStore />

          <S.PageMaxWidth>
            {
              [
                'aliexpress-products'
              ].includes(window.location.pathname.split("/")[1]) && (
                <TryForFreeBanner />
              )
            }
            {children}

            <BottomMenu />
          </S.PageMaxWidth>
        </S.PageWrapper>
      ) : (
        <Onboarding
          isShowing={isShowingOnboarding}
          onFinish={() => {
            if (DISABLE_PAYMENTS) {
              setIsShowingOnboarding(false);
            } else {
              navigate(
                {
                  pathname: paths.checkout.index,
                },
                { state: { selectedPlanId: empirePlan?.id || "" } }
              );
            }
          }}
          onGoToApp={handleGoToApp}
        />
      )}
    </>
  );
};

export const PageWrapperFullScreen: React.FC<IProps> = ({
  children = null,
}) => {
  const pageLocation = useLocation();

  useEffect(() => {
    Userpilot.reload();
  }, [pageLocation]);

  return (
    <S.PageWrapperFullscreen>
      <PlansModal />
      <AnualPlansModal />
      <ResumeModal />
      <LimitsModal />
      <>{children}</>
    </S.PageWrapperFullscreen>
  );
};
